import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Home, Shield, Lock, Eye, Play, RotateCcw, Zap, Trophy, AlertTriangle } from 'lucide-react';
import { useAcademyStore } from '../../store/academyStore';

interface Attack {
    id: number;
    type: 'data-breach' | 'ransomware' | 'ddos' | 'insider' | 'phishing';
    x: number;
    progress: number;
    blocked: boolean;
    requiredDefense: 'C' | 'I' | 'A';
}

interface Defense {
    type: 'C' | 'I' | 'A';
    position: number;
    cooldown: number;
}

const ATTACK_TYPES = {
    'data-breach': { color: 'bg-red-500', icon: '🔓', name: 'Data Breach', threat: 'C', damage: 20 },
    'ransomware': { color: 'bg-purple-500', icon: '💀', name: 'Ransomware', threat: 'I', damage: 25 },
    'ddos': { color: 'bg-orange-500', icon: '🌊', name: 'DDoS Attack', threat: 'A', damage: 15 },
    'insider': { color: 'bg-yellow-500', icon: '🕵️', name: 'Insider Threat', threat: 'C', damage: 30 },
    'phishing': { color: 'bg-pink-500', icon: '🎣', name: 'Phishing', threat: 'I', damage: 20 },
};

const DEFENSE_TYPES = {
    'C': { name: 'Confidentiality', color: 'from-blue-500 to-blue-700', icon: <Lock size={24} />, description: 'Encryption, Access Control' },
    'I': { name: 'Integrity', color: 'from-green-500 to-green-700', icon: <Shield size={24} />, description: 'Hashing, Digital Signatures' },
    'A': { name: 'Availability', color: 'from-yellow-500 to-amber-600', icon: <Zap size={24} />, description: 'Redundancy, Load Balancing' },
};

export const HackDefense = () => {
    const { completeModule } = useAcademyStore();
    const [isPlaying, setIsPlaying] = useState(false);
    const [score, setScore] = useState(0);
    const [highScore, setHighScore] = useState(() => {
        const saved = localStorage.getItem('hack-defense-highscore');
        return saved ? parseInt(saved) : 0;
    });
    const [health, setHealth] = useState(100);
    const [wave, setWave] = useState(1);
    const [attacks, setAttacks] = useState<Attack[]>([]);
    const [selectedDefense, setSelectedDefense] = useState<'C' | 'I' | 'A'>('C');
    const [gameOver, setGameOver] = useState(false);
    const [defenseSlots, setDefenseSlots] = useState<(Defense | null)[]>([null, null, null, null, null]);
    const [attacksBlocked, setAttacksBlocked] = useState(0);
    const [showTutorial, setShowTutorial] = useState(true);

    const spawnAttack = useCallback(() => {
        const types = Object.keys(ATTACK_TYPES) as Attack['type'][];
        const type = types[Math.floor(Math.random() * types.length)];
        const lane = Math.floor(Math.random() * 5);

        const attack: Attack = {
            id: Date.now() + Math.random(),
            type,
            x: lane,
            progress: 0,
            blocked: false,
            requiredDefense: ATTACK_TYPES[type].threat as 'C' | 'I' | 'A',
        };

        setAttacks(prev => [...prev, attack]);
    }, []);

    // Attack spawner
    useEffect(() => {
        if (!isPlaying) return;
        const interval = setInterval(spawnAttack, Math.max(2000 - wave * 200, 800));
        return () => clearInterval(interval);
    }, [isPlaying, spawnAttack, wave]);

    // Attack movement
    useEffect(() => {
        if (!isPlaying) return;

        const interval = setInterval(() => {
            setAttacks(prev => {
                const updated = prev.map(attack => {
                    if (attack.blocked) return attack;

                    // Check if defense blocks it
                    const defense = defenseSlots[attack.x];
                    if (defense && defense.type === attack.requiredDefense && attack.progress >= 60 && attack.progress <= 80) {
                        return { ...attack, blocked: true };
                    }

                    return { ...attack, progress: attack.progress + 2 };
                });

                // Handle attacks that reached the end
                updated.forEach(attack => {
                    if (attack.progress >= 100 && !attack.blocked) {
                        const damage = ATTACK_TYPES[attack.type].damage;
                        setHealth(h => Math.max(0, h - damage));
                    }
                });

                // Count blocked attacks
                const newlyBlocked = updated.filter(a => a.blocked && !prev.find(p => p.id === a.id && p.blocked));
                if (newlyBlocked.length > 0) {
                    setScore(s => s + newlyBlocked.length * 25);
                    setAttacksBlocked(ab => ab + newlyBlocked.length);
                }

                return updated.filter(a => a.progress < 100 || a.blocked);
            });
        }, 50);

        return () => clearInterval(interval);
    }, [isPlaying, defenseSlots]);

    // Remove blocked attacks after animation
    useEffect(() => {
        const interval = setInterval(() => {
            setAttacks(prev => prev.filter(a => !a.blocked || a.progress < 90));
        }, 1000);
        return () => clearInterval(interval);
    }, []);

    // Wave progression
    useEffect(() => {
        if (!isPlaying) return;
        const interval = setInterval(() => {
            setWave(w => w + 1);
            setScore(s => s + 50); // Wave bonus
        }, 15000);
        return () => clearInterval(interval);
    }, [isPlaying]);

    // Game over check
    useEffect(() => {
        if (health <= 0 && isPlaying) {
            setGameOver(true);
            setIsPlaying(false);
            if (score > highScore) {
                setHighScore(score);
                localStorage.setItem('hack-defense-highscore', score.toString());
            }
            if (wave >= 3) {
                completeModule('cia-triad', 150);
            }
        }
    }, [health, isPlaying, score, highScore, wave, completeModule]);

    const placeDefense = (slotIndex: number) => {
        if (!isPlaying) return;

        const newSlots = [...defenseSlots];
        newSlots[slotIndex] = { type: selectedDefense, position: slotIndex, cooldown: 0 };
        setDefenseSlots(newSlots);
    };

    const startGame = () => {
        setIsPlaying(true);
        setScore(0);
        setHealth(100);
        setWave(1);
        setAttacks([]);
        setGameOver(false);
        setDefenseSlots([null, null, null, null, null]);
        setAttacksBlocked(0);
        setShowTutorial(false);
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-red-900/20 to-slate-900 font-sans text-white">
            {/* Header */}
            <div className="bg-slate-800/80 backdrop-blur-xl border-b border-slate-700 px-8 py-4 flex justify-between items-center sticky top-0 z-50">
                <Link to="/" className="flex items-center gap-2 text-slate-400 font-bold hover:text-red-400 transition-colors">
                    <Home size={20} /> Dashboard
                </Link>
                <div className="bg-red-900/50 text-red-400 px-4 py-2 rounded-xl text-base font-bold border border-red-800 flex items-center gap-2">
                    <Shield size={20} /> Hack Defense
                </div>
            </div>

            <div className="max-w-6xl mx-auto p-8">
                {/* Title */}
                <div className="text-center mb-8">
                    <h1 className="text-5xl font-black text-white mb-3 flex items-center justify-center gap-4">
                        <Shield className="text-red-400" size={48} /> Hack Defense
                    </h1>
                    <p className="text-xl text-slate-400 max-w-2xl mx-auto">
                        Defend your network using the CIA Triad! Place defenses to block incoming attacks.
                    </p>
                </div>

                {/* Stats */}
                <div className="flex justify-center gap-6 mb-8 flex-wrap">
                    <div className="bg-slate-800 px-8 py-4 rounded-2xl border border-slate-700 text-center min-w-[140px]">
                        <div className="text-4xl font-black text-green-400">{score}</div>
                        <div className="text-sm text-slate-500 font-bold uppercase">Score</div>
                    </div>
                    <div className="bg-slate-800 px-8 py-4 rounded-2xl border border-slate-700 text-center min-w-[140px]">
                        <div className={`text-4xl font-black ${health > 50 ? 'text-green-400' : health > 25 ? 'text-yellow-400' : 'text-red-400'}`}>
                            {health}%
                        </div>
                        <div className="text-sm text-slate-500 font-bold uppercase">Health</div>
                    </div>
                    <div className="bg-slate-800 px-8 py-4 rounded-2xl border border-slate-700 text-center min-w-[140px]">
                        <div className="text-4xl font-black text-purple-400">Wave {wave}</div>
                        <div className="text-sm text-slate-500 font-bold uppercase">Current</div>
                    </div>
                    <div className="bg-slate-800 px-8 py-4 rounded-2xl border border-slate-700 text-center min-w-[140px]">
                        <div className="text-4xl font-black text-cyan-400">{attacksBlocked}</div>
                        <div className="text-sm text-slate-500 font-bold uppercase">Blocked</div>
                    </div>
                    <div className="bg-slate-800 px-8 py-4 rounded-2xl border border-slate-700 text-center min-w-[140px]">
                        <div className="text-4xl font-black text-yellow-400">{highScore}</div>
                        <div className="text-sm text-slate-500 font-bold uppercase">High Score</div>
                    </div>
                </div>

                {/* Defense Selector */}
                <div className="flex justify-center gap-4 mb-6">
                    {(['C', 'I', 'A'] as const).map(type => (
                        <button
                            key={type}
                            onClick={() => setSelectedDefense(type)}
                            disabled={!isPlaying}
                            className={`px-6 py-4 rounded-2xl font-bold text-lg transition-all border-2 flex items-center gap-3 ${selectedDefense === type
                                ? `bg-gradient-to-r ${DEFENSE_TYPES[type].color} text-white border-white/30 shadow-xl scale-105`
                                : 'bg-slate-800 text-slate-400 border-slate-600 hover:border-slate-500'
                                }`}
                        >
                            {DEFENSE_TYPES[type].icon}
                            <div className="text-left">
                                <div>{DEFENSE_TYPES[type].name}</div>
                                <div className="text-xs opacity-70">{DEFENSE_TYPES[type].description}</div>
                            </div>
                        </button>
                    ))}
                </div>

                {/* Health Bar */}
                <div className="mb-6">
                    <div className="h-4 bg-slate-800 rounded-full overflow-hidden border border-slate-700">
                        <motion.div
                            className={`h-full ${health > 50 ? 'bg-green-500' : health > 25 ? 'bg-yellow-500' : 'bg-red-500'}`}
                            animate={{ width: `${health}%` }}
                            transition={{ duration: 0.3 }}
                        />
                    </div>
                    <div className="flex justify-between text-sm text-slate-500 mt-1">
                        <span>Network Health</span>
                        <span>{health}%</span>
                    </div>
                </div>

                {/* Game Board */}
                <div className="relative bg-slate-800/50 rounded-3xl border-2 border-slate-700 overflow-hidden" style={{ height: '400px' }}>
                    {/* Lanes */}
                    <div className="absolute inset-0 flex">
                        {[0, 1, 2, 3, 4].map(lane => (
                            <div
                                key={lane}
                                className="flex-1 border-r border-slate-700/50 relative"
                                onClick={() => placeDefense(lane)}
                            >
                                {/* Defense Zone Indicator */}
                                <div className="absolute left-0 right-0" style={{ top: '60%', height: '20%' }}>
                                    <div className="h-full border-2 border-dashed border-green-500/30 bg-green-500/5 flex items-center justify-center">
                                        {defenseSlots[lane] ? (
                                            <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${DEFENSE_TYPES[defenseSlots[lane]!.type].color} flex items-center justify-center text-white shadow-xl`}>
                                                {DEFENSE_TYPES[defenseSlots[lane]!.type].icon}
                                            </div>
                                        ) : (
                                            <div className="text-slate-600 text-sm font-bold">Click to place</div>
                                        )}
                                    </div>
                                </div>

                                {/* Target Zone */}
                                <div className="absolute bottom-0 left-0 right-0 h-8 bg-red-500/20 border-t-2 border-red-500/50 flex items-center justify-center">
                                    <AlertTriangle className="text-red-500/50" size={16} />
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Attacks */}
                    <AnimatePresence>
                        {attacks.map(attack => (
                            <motion.div
                                key={attack.id}
                                className="absolute -translate-x-1/2"
                                style={{
                                    left: `${(attack.x + 0.5) * 20}%`,
                                    top: `${attack.progress}%`,
                                }}
                                initial={{ scale: 0, opacity: 0 }}
                                animate={{
                                    scale: attack.blocked ? 0 : 1,
                                    opacity: attack.blocked ? 0 : 1,
                                }}
                                exit={{ scale: 0, opacity: 0 }}
                            >
                                <div className={`${ATTACK_TYPES[attack.type].color} w-16 h-16 rounded-xl flex flex-col items-center justify-center shadow-xl border-2 border-white/20`}>
                                    <span className="text-2xl">{ATTACK_TYPES[attack.type].icon}</span>
                                    <span className="text-[10px] font-bold text-white/70">{attack.requiredDefense}</span>
                                </div>
                            </motion.div>
                        ))}
                    </AnimatePresence>

                    {/* Overlay */}
                    {!isPlaying && (
                        <div className="absolute inset-0 bg-slate-900/90 backdrop-blur-sm flex items-center justify-center z-20">
                            <div className="text-center max-w-lg">
                                {gameOver ? (
                                    <>
                                        <div className="text-6xl mb-6">{wave >= 3 ? '🏆' : '💥'}</div>
                                        <h2 className="text-4xl font-bold text-white mb-3">Network Breached!</h2>
                                        <p className="text-xl text-slate-400 mb-2">Score: <span className="text-green-400 font-bold">{score}</span></p>
                                        <p className="text-lg text-slate-500 mb-2">Waves Survived: {wave}</p>
                                        <p className="text-lg text-slate-500 mb-6">Attacks Blocked: {attacksBlocked}</p>
                                    </>
                                ) : showTutorial ? (
                                    <>
                                        <div className="text-6xl mb-6">🛡️</div>
                                        <h2 className="text-4xl font-bold text-white mb-3">How to Play</h2>
                                        <div className="text-left text-slate-400 mb-6 space-y-3">
                                            <p>1. <strong className="text-white">Select a defense</strong> (C, I, or A) from the buttons above</p>
                                            <p>2. <strong className="text-white">Click lanes</strong> to place defenses in the green zone</p>
                                            <p>3. <strong className="text-white">Match the defense</strong> to the attack's requirement (shown on each attack)</p>
                                            <p className="text-yellow-400">🔓 = Needs C (Confidentiality) | 🛡️ = Needs I (Integrity) | ⚡ = Needs A (Availability)</p>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="text-6xl mb-6">🛡️</div>
                                        <h2 className="text-4xl font-bold text-white mb-3">Hack Defense</h2>
                                        <p className="text-xl text-slate-400 mb-6">Defend your network with CIA Triad!</p>
                                    </>
                                )}
                                <button
                                    onClick={startGame}
                                    className="btn btn-lg bg-gradient-to-r from-red-500 to-rose-600 text-white shadow-xl hover:shadow-2xl hover:scale-105 transition-all"
                                >
                                    <Play size={24} fill="currentColor" /> {gameOver ? 'Play Again' : 'Start Defense'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Attack Legend */}
                <div className="mt-8 bg-slate-800/50 rounded-2xl p-6 border border-slate-700">
                    <h3 className="font-bold text-white text-lg mb-4">⚔️ Threat Types</h3>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                        {Object.entries(ATTACK_TYPES).map(([key, value]) => (
                            <div key={key} className={`p-4 rounded-xl ${value.color} bg-opacity-20`}>
                                <div className="text-2xl mb-1">{value.icon}</div>
                                <div className="font-bold text-white">{value.name}</div>
                                <div className="text-sm text-white/70">Needs: {value.threat}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};
